import { type UseQueryResult } from "@tanstack/react-query";
import { Link } from "react-router";

import { ExplainedBadge } from "@/shared/components/ExplainedBadge";
import { completeSessionBadge } from "@/shared/components/sessions";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import {
  type SessionIngestionStatus,
  type SessionRecord,
  skippedFileCount,
  tracksCompletion,
} from "@/shared/domain/sessions";
import {
  useIsSessionComplete,
  useSessionIngestionStatus,
} from "@/shared/domain/sessions/hooks";
import { type BadgeVariant } from "@/shared/foundation/primitives";

import { CompletionDescription } from "./SessionCompletion";
import {
  fileCount,
  ingestionCountsSentence,
  ingestionRunFailed,
  sessionIngestionPanelLink,
} from "./sessionIngestion";

/**
 * Whether the session is in progress, complete with files still ingesting,
 * or fully ingested. Hovering or clicking it says what that means. Hidden in
 * orgs without session ingestion, where no session can be marked complete.
 */
export function SessionStatusBadge({ session }: { session: SessionRecord }) {
  const released = useCheckFeatureFlag(
    "release-session-ingestion",
    session.org_id,
  );
  const showsInProgress = released && tracksCompletion(session);
  const status = useSessionIngestionStatus({
    session,
    enabled: session.status === "complete" || showsInProgress,
  });
  const complete = useIsSessionComplete(session, status);
  if (complete) {
    return <CompleteBadge session={session} status={status} />;
  }
  if (!showsInProgress) {
    return null;
  }
  return <InProgressBadge session={session} status={status.data} />;
}

function InProgressBadge({
  session,
  status,
}: {
  session: SessionRecord;
  status: SessionIngestionStatus | undefined;
}) {
  return (
    <ExplainedBadge
      label="In progress"
      variant="info"
      aria-label="In progress: what does this mean?"
      explanation={
        <>
          <p>
            Files can still be added to this session until it&rsquo;s marked
            complete. You can view and download its data now.
          </p>
          <p>
            {session.completion_policy === null ? (
              "It stays in progress until someone marks it complete."
            ) : (
              <>
                <CompletionDescription session={session} />.
              </>
            )}
          </p>
          {status !== undefined && status.pending_file_count > 0 && (
            <p data-testid="session-status-ingestion">
              <Link to={sessionIngestionPanelLink(session.session_id)}>
                {fileCount(status.pending_file_count)}
              </Link>{" "}
              not ingested yet.
            </p>
          )}
          <IngestionCounts status={status} />
        </>
      }
      data-testid="session-status-badge"
    />
  );
}

/**
 * The complete badge's label. Never plain "Complete": until the ingestion
 * status loads, the badge can't say whether the data is ready.
 */
function completeBadge(
  status: SessionIngestionStatus | undefined,
  failedToLoad: boolean,
): { label: string; variant: BadgeVariant } {
  if (status === undefined && failedToLoad) {
    return { label: "Ingestion status unknown", variant: "secondary" };
  }
  if (status === undefined || status.state === "in_progress") {
    return { label: "Checking ingestion…", variant: "secondary" };
  }
  return completeSessionBadge({
    ...status,
    skipped_file_count: skippedFileCount(status),
    failed_file_count: status.pending_files.filter(ingestionRunFailed).length,
  });
}

/** A complete session's badge, which follows its ingestion as it finishes. */
function CompleteBadge({
  session,
  status,
}: {
  session: SessionRecord;
  status: UseQueryResult<SessionIngestionStatus, Error>;
}) {
  const { label, variant } = completeBadge(status.data, status.isError);
  return (
    <ExplainedBadge
      label={label}
      variant={variant}
      aria-label={`${label}: what does this mean?`}
      explanation={
        <>
          {/* A div: the principal link renders one, which a <p> can't hold. */}
          <div>
            <CompletionDescription session={session} />.
          </div>
          {status.data === undefined && status.isError && (
            <p>Could not load whether its files are ingested.</p>
          )}
          {status.data !== undefined && (
            <CompleteIngestion session={session} status={status.data} />
          )}
          <IngestionCounts status={status.data} />
          <p>Adding a file puts the session back in progress.</p>
        </>
      }
      data-testid="session-status-badge"
    />
  );
}

function CompleteIngestion({
  session,
  status,
}: {
  session: SessionRecord;
  status: SessionIngestionStatus;
}) {
  const panel = sessionIngestionPanelLink(session.session_id);
  if (status.state === "processing") {
    const failed = status.pending_files.filter(ingestionRunFailed).length;
    return (
      <>
        <p data-testid="session-status-ingestion">
          <Link to={panel}>{fileCount(status.pending_file_count)} left</Link> to
          ingest. After they are ingested, the session counts as fully ingested.
        </p>
        {failed > 0 && (
          <p data-testid="session-status-failed">
            Ingestion failed on {failed} of them. Run it again, or stop waiting
            for them, from the <Link to={panel}>list of files</Link>.
          </p>
        )}
      </>
    );
  }
  if (status.state !== "ingested") {
    return null;
  }
  const skipped = skippedFileCount(status);
  if (status.ingested_file_count === 0 && status.pending_file_count === 0) {
    return (
      <p data-testid="session-status-ingestion">
        No file in this session was ingested, and Roboto is not waiting for any.
      </p>
    );
  }
  return (
    <>
      <p data-testid="session-status-ingestion">
        Every file Roboto ingests is ingested, so the session counts as fully
        ingested.
      </p>
      {skipped > 0 && (
        <p data-testid="session-status-skipped">
          Roboto did not wait for <Link to={panel}>{fileCount(skipped)}</Link>{" "}
          because someone chose not to wait for {skipped === 1 ? "it" : "them"}.
        </p>
      )}
    </>
  );
}

function IngestionCounts({
  status,
}: {
  status: SessionIngestionStatus | undefined;
}) {
  if (status === undefined) {
    return null;
  }
  const counts = ingestionCountsSentence(status);
  return (
    <>
      {counts !== null && (
        <p data-testid="session-status-counts">Files: {counts}</p>
      )}
      <p>A file is ingested when its signals can be plotted and searched.</p>
    </>
  );
}
